import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Col, DatePicker, Modal, Row, TimePicker, message } from 'antd'
import moment from 'moment'
import FloatingInput from '../../components/FloatingInput'
import { BASE_URL } from '../../config'
import http from '../../utils/api'
import { validURL } from '../../utils/helpers'
import BetterFloatingInput from '../BetterFloatingInput'
import UploadFileField from '../Forms/Fields/UploadFileField'
import Timezone from '../SelectTimezoneComponent/SelectTimezoneComponent'

const MainInfoModal = ({ visible, handleOk, handleCancel, event }) => {
  const [data, setData] = useState({
    ...event,
    meetingPeriods: [],
  })

  const updateData = (field, value) => {
    const newState = { ...data }
    newState[field] = value
    setData(newState)
  }

  const saveChanges = () => {
    if (validURL(data.websiteUrl) && validURL(data.registrationUrl)) {
      const dataToSend = {
        ...data,
        startDate: data.startDate
          ? moment
              .tz(data.startDate, data.timeZone)
              .utc()
              .format('YYYY-MM-DDTHH:mm:ss')
          : null,
        endDate: data.endDate
          ? moment
              .tz(data.endDate, data.timeZone)
              .utc()
              .format('YYYY-MM-DDTHH:mm:ss')
          : null,
        schedulerAccessDate: data.schedulerAccessDate
          ? moment
              .tz(data.schedulerAccessDate, data.timeZone)
              .utc()
              .format('YYYY-MM-DDTHH:mm:ss')
          : null,
        meetingPeriods: data.meetingPeriods
          .map((p) => {
            if (
              !p.date ||
              !p.earliestMeetingStartTime ||
              !p.latestMeetingEndTime
            )
              return null

            const dateStr = moment(p.date).format('YYYY-MM-DD')

            const fullStart = moment.tz(
              `${dateStr} ${p.earliestMeetingStartTime.format('HH:mm:ss')}`,
              'YYYY-MM-DD HH:mm:ss',
              data.timeZone
            )
            const fullEnd = moment.tz(
              `${dateStr} ${p.latestMeetingEndTime.format('HH:mm:ss')}`,
              'YYYY-MM-DD HH:mm:ss',
              data.timeZone
            )

            return {
              eventMeetingPeriodId: p.eventMeetingPeriodId,
              date: dateStr,
              earliestMeetingStartTime: fullStart.utc().format('HH:mm:00'),
              latestMeetingEndTime: fullEnd.utc().format('HH:mm:00'),
            }
          })
          .filter(Boolean),
      }

      http.put('/events/update', dataToSend).then((response) => {
        message.success('Summit information updated')
        handleOk()
      })
    }
  }

  const onClose = () => {
    handleCancel()
  }

  useEffect(() => {
    if (event != null) {
      setData({
        ...event,
        startDate: event.startDate
          ? moment.utc(event.startDate).tz(event.timeZone)
          : null,
        endDate: event.endDate
          ? moment.utc(event.endDate).tz(event.timeZone)
          : null,
        schedulerAccessDate: event.schedulerAccessDate
          ? moment.utc(event.schedulerAccessDate).tz(event.timeZone)
          : null,
        meetingPeriods:
          Array.isArray(event.meetingPeriods) && event.meetingPeriods.length > 0
            ? event.meetingPeriods
                .map((p) => {
                  const pdate = moment.utc(p.date).format('YYYY-MM-DD')
                  const fullStart =
                    pdate && p.earliestMeetingStartTime
                      ? moment
                          .utc(`${pdate}T${p.earliestMeetingStartTime}`)
                          .tz(event.timeZone)
                      : null
        
                  const fullEnd =
                    pdate && p.latestMeetingEndTime
                      ? moment
                          .utc(`${pdate}T${p.latestMeetingEndTime}`)
                          .tz(event.timeZone)
                      : null
        
                  return {
                    eventMeetingPeriodId: p.eventMeetingPeriodId,
                    date: p.date,
                    earliestMeetingStartTime: fullStart,
                    latestMeetingEndTime: fullEnd,
                  }
                })
                .sort((a, b) => {
                  if (!a.earliestMeetingStartTime || !b.earliestMeetingStartTime)
                    return 0
                  return a.earliestMeetingStartTime.diff(b.earliestMeetingStartTime)
                })
            : [
                {
                  eventMeetingPeriodId: null,
                  date: '',
                  earliestMeetingStartTime: null,
                  latestMeetingEndTime: null,
                },
              ],
      })
    }
  }, [event])

  const addMeetingPeriod = () => {
    setData((prev) => ({
      ...prev,
      meetingPeriods: [
        ...prev.meetingPeriods,
        {
          eventMeetingPeriodId: null,
          date: '',
          earliestMeetingStartTime: '',
          latestMeetingEndTime: '',
        },
      ],
    }))
  }

  const updateMeetingPeriod = (index, field, value) => {
    const updated = [...data.meetingPeriods]
    updated[index][field] = value
    setData((prev) => ({ ...prev, meetingPeriods: updated }))
  }

  const removeMeetingPeriod = (index) => {
    const updated = [...data.meetingPeriods]
    updated.splice(index, 1)
    setData((prev) => ({ ...prev, meetingPeriods: updated }))
  }

  return (
    <Modal
      className="cc-modal-accept"
      title="Edit Summit Main Information"
      visible={visible}
      width={1000}
      okText="Save Changes"
      cancelText="Close"
      onOk={saveChanges}
      onCancel={onClose}
      okButtonProps={{
        disabled: false,
        style: { backgroundColor: '#2FC5D2', borderStyle: 'none' },
      }}
      centered={true}
      cancelButtonProps={{ disabled: false }}
    >
      <div style={{ padding: '13px 16px 32px 32px' }}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={24}>
            <FloatingInput
              value={data.name}
              name="Name"
              nameField="Name"
              onChange={(e) => updateData('name', e)}
            />
          </Col>
          <Col span={12}>
            <FloatingInput
              value={data.address}
              name="Address"
              nameField="Address"
              onChange={(e) => updateData('address', e)}
            />
          </Col>
          <Col span={12}>
            <FloatingInput
              value={data.state}
              name="State"
              nameField="State"
              onChange={(e) => updateData('state', e)}
            />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <FloatingInput
              value={data.city}
              name="City"
              nameField="City"
              onChange={(e) => updateData('city', e)}
            />
          </Col>
          <Col span={12}>
            <FloatingInput
              value={data.venue}
              name="Venue"
              nameField="Venue"
              onChange={(e) => updateData('venue', e)}
            />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <BetterFloatingInput
              onChange={(e) => updateData('websiteUrl', e.target.value)}
              isValid={validURL(data.websiteUrl)}
              validationMessage="Please enter a valid url"
              value={data.websiteUrl}
              name="Website Url"
            />
          </Col>
          <Col span={12}>
            <BetterFloatingInput
              onChange={(e) => updateData('registrationUrl', e.target.value)}
              isValid={validURL(data.registrationUrl)}
              validationMessage="Please enter a valid url"
              value={data.registrationUrl}
              name="Registration Url"
            />
          </Col>
        </Row>

        {!event.virtualSummit && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <h3>Summit Time Zone:</h3>
              <Timezone
                value={data.timeZone}
                handleTimezoneChange={(e) => updateData('timeZone', e)}
                shouldHaveDefaultValue={false}
              />
            </Col>
          </Row>
        )}

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <h3>Summit Start - End Dates:</h3>
            <DatePicker
              showTime
              className="cc-background-input"
              size="large"
              width="100%"
              defaultValue={data.startDate}
              onChange={(value) => updateData('startDate', value)}
            />
          </Col>
          <Col span={6} style={{ paddingTop: '30px' }}>
            <DatePicker
              showTime
              className="cc-background-input"
              size="large"
              width="100%"
              defaultValue={data.endDate}
              onChange={(value) => updateData('endDate', value)}
            />
          </Col>
          <Col span={6}>
            <h3>Scheduler Access Date:</h3>
            <DatePicker
              showTime
              className="cc-background-input"
              size="large"
              width="100%"
              defaultValue={data.schedulerAccessDate}
              onChange={(value) => updateData('schedulerAccessDate', value)}
            />
          </Col>
          <Col span={6}>
            <h3>Time slot size:</h3>
            <FloatingInput
              value={data.meetingDurationInMinutes}
              name="Meeting Duration (minutes)"
              nameField="Meeting Duration (minutes)"
              disabled
            />
          </Col>
        </Row>

        <Row style={{ marginBottom: 16 }}>
          <Col span={24}>
            <h3>Meeting Periods</h3>
            {data.meetingPeriods.map((period, index) => (
              <Row style={{ marginBottom: 8 }} key={index}>
                <Col span={6}>
                  <DatePicker
                    placeholder="Date"
                    width="100%"
                    size="large"
                    value={period.date ? moment(period.date) : null}
                    onChange={(val) =>
                      updateMeetingPeriod(
                        index,
                        'date',
                        val ? val.format('YYYY-MM-DD') : ''
                      )
                    }
                  />
                </Col>
                <Col span={6}>
                  <TimePicker
                    use24Hours
                    size="large"
                    style={{ width: '100%' }}
                    format="h:mm a"
                    minuteStep={5}
                    placeholder="Start"
                    value={period.earliestMeetingStartTime}
                    onChange={(val) =>
                      updateMeetingPeriod(
                        index,
                        'earliestMeetingStartTime',
                        val ?? null
                      )
                    }
                  />
                </Col>
                <Col span={6}>
                  <TimePicker
                    use24Hours
                    size="large"
                    style={{ width: '100%' }}
                    format="h:mm a"
                    minuteStep={5}
                    placeholder="End"
                    value={period.latestMeetingEndTime}
                    onChange={(val) =>
                      updateMeetingPeriod(
                        index,
                        'latestMeetingEndTime',
                        val ?? null
                      )
                    }
                  />
                </Col>
                <Col span={4}>
                  <button
                    onClick={addMeetingPeriod}
                    style={{
                      width: 38,
                      height: 38,
                      border: '1px solid #2FC5D2',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      color: '#2FC5D2',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = '#e6f9fb')
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.backgroundColor = 'white')
                    }
                  >
                    +
                  </button>

                  {data.meetingPeriods.length > 1 && (
                    <button
                      onClick={() => removeMeetingPeriod(index)}
                      style={{
                        width: 38,
                        height: 38,
                        border: '1px solid #2FC5D2',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        color: '#2FC5D2',
                        fontWeight: 'bold',
                        marginLeft: 8,
                        cursor: 'pointer',
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.backgroundColor = '#e6f9fb')
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.backgroundColor = 'white')
                      }
                    >
                      –
                    </button>
                  )}
                </Col>
              </Row>
            ))}
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <h3>Poster Image</h3>
            <UploadFileField
              accept=".jpg, .png, .bmp"
              listType="picture"
              hint="File Types: .PNG, .JPG Max File Size: 4MB"
              action={`${BASE_URL}uploads`}
              value={data.posterImage}
              name="Logo"
              onChange={(e) => updateData('posterImage', e)}
            />
          </Col>
          <Col span={12}>
            <h3>Thumbnail Image</h3>
            <UploadFileField
              accept=".jpg, .png, .bmp"
              listType="picture"
              hint="File Types: .PNG, .JPG Max File Size: 4MB"
              action={`${BASE_URL}uploads`}
              value={data.thumbnailImage}
              name="Logo"
              onChange={(e) => updateData('thumbnailImage', e)}
            />
          </Col>
        </Row>
      </div>
    </Modal>
  )
}

MainInfoModal.MainInfoModal = {
  visible: PropTypes.bool.isRequired,
  handleOk: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  event: PropTypes.number.isRequired,
}

export default MainInfoModal
